import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { FertilizerActions } from '@galo-monitor/stores/fertilizer'
import DataTable from '@galo-monitor/web-components/DataTable'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import FertilizerModal from '@galo-monitor/web-containers/modals/root/FertilizerModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Fertilizers = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: fertilizers, mutate, isValidating } = useGaloMonitorApi('/admin/fertilizers')

  const userActions = useMemo(
    () => getActionsBySlug(routes.fertilizers.slug),
    [getActionsBySlug, routes.fertilizers.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('trade name'),
        field: 'name'
      },
      {
        title: t('fertilizer type'),
        field: 'fertilizerType',
        render: ({ fertilizerType }) => t(fertilizerType)
      },
      {
        title: t('origin'),
        field: 'origin',
        render: ({ origin }) => t(origin)
      },
      {
        title: t('manufacturer', { howMany: 1 }),
        field: 'fertilizerMaker'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-fertilizer',
        Component: FertilizerModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, fertilizer) => {
      createDialog({
        id: 'edit-fertilizer',
        Component: FertilizerModal,
        props: {
          fertilizer,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, fertilizer) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('fertilizer', { howMany: 1 }),
            name: fertilizer.name
          }),
          onConfirm: () => {
            dispatch(FertilizerActions.removeFertilizer(
              fertilizer,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: toLower(t('fertilizer', { howMany: 1 })),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              ({ response }) => {
                createDialog({
                  id: 'fertilizer-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('fertilizer', { howMany: 1 }))
                    })  }.`,
                    message: `${ t(response.data.message) }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ fertilizers || [] }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

export default Fertilizers
